import { getBackendUrl } from '@/utils/apiUrl';
import React, { useState } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Mail, Phone, Book, Video, Send, HelpCircle } from 'lucide-react';

export const Support = ({ user }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${getBackendUrl()}/api/support/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ subject, message })
      });

      if (response.ok) {
        toast.success('✅ Support-Anfrage gesendet!');
        setSubject('');
        setMessage('');
      } else {
        toast.error('❌ Fehler beim Senden');
      }
    } catch (error) {
      toast.error('❌ Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  const supportOptions = [
    {
      icon: Mail,
      title: 'E-Mail Support',
      description: 'support@easyclaim24.com',
      color: '#3B82F6',
      bgColor: '#DBEAFE',
      action: () => window.location.href = 'mailto:support@easyclaim24.com'
    },
    {
      icon: Phone,
      title: 'Telefon Support',
      description: '+49 30 12345678',
      color: '#10B981',
      bgColor: '#D1FAE5',
      action: () => window.location.href = 'tel:+493012345678'
    },
    {
      icon: Book,
      title: 'Dokumentation',
      description: 'Handbücher & Guides',
      color: '#6366F1',
      bgColor: '#E0E7FF'
    },
    {
      icon: Video,
      title: 'Video Tutorials',
      description: 'Schritt-für-Schritt Anleitungen',
      color: '#F59E0B',
      bgColor: '#FEF3C7'
    }
  ];

  const faqs = [
    {
      question: 'Wie erstelle ich eine neue Akte?',
      answer: 'Klicken Sie auf den "Neue Akte" Button im Header oder navigieren Sie zu Akten > Neue Akte.'
    },
    {
      question: 'Wie kann ich Kundendaten importieren?',
      answer: 'Gehen Sie zu Kunden und nutzen Sie die Upload-Funktion für XML, CSV oder JSON Dateien.'
    },
    {
      question: 'Wo finde ich meine Aufträge?',
      answer: 'Alle Aufträge finden Sie unter dem Menüpunkt "Akten" in der Übersicht.'
    }
  ];

  return (
    <Layout user={user}>
      <div className="w-full max-w-full">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Support</h1>
          <p className="text-sm text-gray-600 mt-1">Wir helfen Ihnen gerne weiter</p>
        </div>

        {/* Support Options Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {supportOptions.map((option, idx) => {
            const Icon = option.icon;
            return (
              <div
                key={idx}
                onClick={option.action}
                className="bg-white rounded-lg border p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                style={{ borderColor: '#E1E4E8' }}
                data-testid={`support-option-${idx}`}
              >
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                  style={{ backgroundColor: option.bgColor }}
                >
                  <Icon className="w-6 h-6" style={{ color: option.color }} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{option.title}</h3>
                <p className="text-sm text-gray-600">{option.description}</p>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Support Request Form */}
          <div className="bg-white rounded-lg shadow-sm border p-6" style={{ borderColor: '#E1E4E8' }}>
            <div className="flex items-center gap-2 mb-4">
              <Send className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Support-Anfrage senden</h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Betreff</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Worum geht es?"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                  data-testid="support-subject-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nachricht</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Beschreiben Sie Ihr Anliegen..."
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                  required
                  data-testid="support-message-textarea"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 text-white font-medium rounded-lg transition-colors"
                style={{ backgroundColor: '#3B82F6' }}
                data-testid="send-support-request-button"
              >
                <Send className="w-4 h-4" />
                {loading ? 'Wird gesendet...' : 'Anfrage senden'}
              </button>
            </form>
          </div>

          {/* FAQ Section */}
          <div className="bg-white rounded-lg shadow-sm border p-6" style={{ borderColor: '#E1E4E8' }}>
            <div className="flex items-center gap-2 mb-4">
              <HelpCircle className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Häufig gestellte Fragen</h2>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="border-b border-gray-200 pb-4 last:border-0 last:pb-0">
                  <h3 className="font-medium text-gray-900 mb-2">{faq.question}</h3>
                  <p className="text-sm text-gray-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Support;
